import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useStore } from '../store/useStore';
import { BlishAPI } from '../apis';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useNotifications({ onChallengeInvite, onChallengeAccepted } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    const token = useStore.getState().accessToken;
    if (!token) return;

    // Convert HTTP URL to WebSocket URL
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/notifications?token=${token}`);

      let everConnected = false;

      wsRef.current.onopen = () => {
        setConnected(true);
        everConnected = true;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              break;
            
            case 'unread_count':
              setUnreadCount(data.data.count);
              break;
            
            case 'notification':
              if (data.data?.type === 'challenge_received' && onChallengeInvite) {
                onChallengeInvite(data.data.data);
              } else if (data.data?.type === 'challenge_accepted' && onChallengeAccepted) {
                onChallengeAccepted(data.data.data);
              } else {
                toast(data.data.title, {
                  description: data.data.message,
                  duration: 5000
                });
              }
              setUnreadCount(prev => prev + 1);
              break;
            
            case 'pong':
              break;
            
            default:
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        if (everConnected) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = () => {};
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const markAsRead = useCallback((notificationId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_read',
        notification_id: notificationId
      }));
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_all_read'
      }));
    }
  }, []);

  // Heartbeat to keep connection alive
  useEffect(() => {
    const heartbeat = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(heartbeat);
  }, []);

  // Connect on mount if token exists
  useEffect(() => {
    const token = useStore.getState().accessToken;
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    unreadCount,
    connected,
    connect,
    disconnect,
    markAsRead,
    markAllAsRead
  };
}
